// A demo of the grant reminders, for the LACW preview's test mode.
//
// One click clears this browser's test data (Aid Planner matters, the current diary week,
// the file requests) and loads made-up LACW matters built so that every kind of reminder
// shows, each at a different urgency. Every example's notes say what it should show and
// where. Nothing here reaches the portal database: test mode never syncs.
//
// Dates are set relative to today, so the demo reads correctly whenever it is loaded.

import { createPlanner } from "./aidPlannerSchema.js";

export const DEMO_FILE_REQUESTS_KEY = "cls_lacw_local_test_file_requests_v1";
const AID_PLANNER_KEY = "aid-planner-matters-v1";
const AID_PLANNER_LAST_OPENED_KEY = "aid-planner-last-opened-v1";
const LACW_DIARY_ACTIVE_KEY = "cls_lacw_aid_diary_active_v1";

const DAY_MS = 24 * 60 * 60 * 1000;

function addDays(today, days) {
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + days);
}
const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const auDate = (date) => `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;

const TABLE_A = [{ court: "Magistrates' Court", grant: "summary hearing", feeBasis: "Lump sum", feeSchedule: "1", feeTable: "A", qualifier: "", aidType: "Summary Single" }];
const claim = (appearanceType) => ({ appearanceType, feeType: appearanceType, claimLodged: true, claimExpected: true });

function matter(id, name, listingInDays, today, { listing, notes, grant = true, claims = [], aid = {}, funding = {}, application = {} }) {
  const planner = createPlanner();
  planner.matterId = id;
  planner.updatedAt = new Date(today.getTime() - DAY_MS).toISOString();
  planner.client.fullName = name;
  Object.assign(planner.matter, {
    matterType: "Criminal",
    court: "Magistrates' Court",
    location: "Melbourne",
    appearanceType: listing,
    nextAppearanceDate: iso(addDays(today, listingInDays)),
    lawyer: "EM",
  });
  if (grant) {
    Object.assign(planner.aid, {
      aidNumber: `26A9${id.replace(/\D/g, "").padStart(5, "0")}`,
      applicationStatus: "Approved",
      aidTypeList: ["Summary Single"],
      approvedGrants: TABLE_A,
    });
    Object.assign(planner.funding, { grantInPlace: true, aidApplied: true });
  }
  Object.assign(planner.aid, aid);
  Object.assign(planner.funding, funding);
  Object.assign(planner.application, application);
  planner.appearanceClaims = claims.map(claim);
  planner.notes = `DEMO: ${notes}`;
  return planner;
}

// Each matter is built to raise one kind of reminder. `expect` is what it should show.
export function buildDemoMatters(today = new Date()) {
  const fiveContestDays = Array(5).fill("DEFEND/CONTEST DAILY FEE");
  return [
    { expect: "extension", planner: matter("demo-1", "Ava Demo", 10, today, {
      listing: "DEFEND/CONTEST DAILY FEE",
      claims: fiveContestDays,
      notes: "All 5 contest days on the Table A grant are claimed and another is listed: Extension required, due now (listed in 10 days).",
    }) },
    { expect: "extension_check", planner: matter("demo-2", "Ben Demo", 25, today, {
      listing: "Plea",
      claims: ["APPEARANCE", "APPEARANCE"],
      notes: "2 of the 3 appearance days are claimed, so this plea uses the last one: Check if extension needed, upcoming (listed in 25 days).",
    }) },
    { expect: "new_grant", planner: matter("demo-3", "Cara Demo", 3, today, {
      listing: "Mention",
      grant: false,
      aid: { applicationStatus: "" },
      notes: "No aid applied for and no grant: New grant required, urgent (listed in 3 days).",
    }) },
    { expect: "not_covered", planner: matter("demo-4", "Dan Demo", 12, today, {
      listing: "Mention",
      aid: { grantExclusions: ["an appearance at a mention hearing"] },
      notes: "The approval letter excludes mention hearings: Not covered by the grant, due now (listed in 12 days).",
    }) },
    { expect: "follow_up", planner: matter("demo-5", "Eve Demo", 20, today, {
      listing: "Plea",
      funding: { extensionRequested: true, extensionDecision: "" },
      notes: "An extension was requested and no decision is recorded: Chase VLA decision, upcoming (listed in 20 days).",
    }) },
    { expect: "condition", planner: matter("demo-6", "Finn Demo", 30, today, {
      listing: "Plea",
      aid: { grantConditions: [{ kind: "proof_of_means", text: "Provide proof of income within 28 days of the grant." }] },
      notes: "The grant asks for proof of means: Grant condition outstanding, upcoming (listed in 30 days).",
    }) },
    { expect: "extension", planner: matter("demo-7", "Gus Demo", -2, today, {
      listing: "DEFEND/CONTEST DAILY FEE",
      claims: fiveContestDays,
      notes: "Like Ava, but the listing was 2 days ago: Extension required, overdue (a late application).",
    }) },
    { expect: "none", planner: matter("demo-8", "Hana Demo", 18, today, {
      listing: "Plea",
      claims: ["APPEARANCE"],
      notes: "A grant in place with fees to spare: no reminder. Its Extension check says Nothing due.",
    }) },
  ];
}

// A week in the LACW Aid Status Diary. Rows for Ava and Hana match Aid Planner matters and
// fill themselves in; the rest show the diary's own reminders and code notes.
export function buildDemoDiaryWeek(today = new Date()) {
  const row = (id, days, clientName, appearanceType, aidStatus, extra = {}) => ({
    id: `demo-row-${id}`,
    date: auDate(addDays(today, days)),
    clientName,
    court: "Melbourne Magistrates' Court",
    appearanceType,
    lawyer: "EM",
    grantType: "",
    aidStatus,
    aidNumber: "",
    confirmed: false,
    confirmedBy: "",
    confirmedAt: "",
    notes: "",
    ...extra,
  });
  return {
    version: 1,
    sourceName: "Demo week (made-up data)",
    savedAt: today.toISOString(),
    entries: [
      row(1, 10, "DEMO, Ava", "CONT", "Not checked", { notes: "Matches Ava in the Aid Planner: fills in as Extension required, with the reason." }),
      row(2, 18, "DEMO, Hana", "PLEA", "Not checked", { notes: "Matches Hana in the Aid Planner: fills in as Aid confirmed with her aid number." }),
      row(3, 4, "Ivy DEMO", "MEN", "Aid not applied for", { notes: "No grant and no application: New grant required, urgent." }),
      row(4, 9, "Jack DEMO", "CONT", "Aid confirmed", { aidNumber: "26A900111", notes: "Adjourned to a contest on a grant: Check if extension needed (a new grant under a consolidated grant)." }),
      row(5, 16, "Kate DEMO", "APP", "Aid confirmed", { aidNumber: "26A900112", notes: "An appeal: New grant required." }),
      row(6, 6, "Liam DEMO", "CM", "Aid confirmed", { aidNumber: "26A900113", notes: "Old code CM: shows 'use COMM'. A committal mention is covered by the grant, so nothing is due." }),
      row(7, 5, "Mia DEMO", "RoD", "Not checked", { notes: "RoD is not a diary code: shows 'Not a diary code'." }),
    ],
  };
}

// New-file requests for the Aid Planner's Applications list. Ava's links to her matter;
// the others show each status and a "Start matter" button.
export function buildDemoFileRequests(today = new Date()) {
  const at = (days) => addDays(today, -days).toISOString();
  return {
    emails: [
      { message_id: "demo-req-1", client_id: "lacw", subject: "New file: Ava Demo vs Police (Melbourne)", snippet: "Contest listed; please open a file.", date: at(40), status: "aid_approved", notes: "" },
      { message_id: "demo-req-2", client_id: "lacw", subject: "New file: Noah Demo", snippet: "Remand matter, first appearance next week.", date: at(1), status: "", notes: "" },
      { message_id: "demo-req-3", client_id: "lacw", subject: "New file: Olive Demo", snippet: "Application lodged Monday.", date: at(5), status: "pending_in_atlas", notes: "Lodged in ATLAS." },
      { message_id: "demo-req-4", client_id: "lacw", subject: "New file: Cara Demo", snippet: "Needs an application.", date: at(2), status: "waiting_to_be_submitted_in_atlas", notes: "" },
    ],
    drafts: [],
  };
}

// Clear this browser's test data and load the demo. storage: { client, device } with
// getItem / setItem / removeItem (clientStorage and deviceStorage in the app).
export function installRemindersDemo({ client, device }, today = new Date()) {
  const matters = Object.fromEntries(buildDemoMatters(today).map(({ planner }) => [planner.matterId, planner]));
  client.setItem(AID_PLANNER_KEY, JSON.stringify(matters));
  client.removeItem(AID_PLANNER_LAST_OPENED_KEY);
  device.setItem(LACW_DIARY_ACTIVE_KEY, JSON.stringify(buildDemoDiaryWeek(today)));
  device.setItem(DEMO_FILE_REQUESTS_KEY, JSON.stringify(buildDemoFileRequests(today)));
  device.removeItem(WALKTHROUGH_KEY);
  return { matters: Object.keys(matters).length };
}

export function readDemoFileRequests(device) {
  try {
    return JSON.parse(device.getItem(DEMO_FILE_REQUESTS_KEY) || "null") || { emails: [], drafts: [] };
  } catch {
    return { emails: [], drafts: [] };
  }
}

export function writeDemoFileRequests(device, data) {
  device.setItem(DEMO_FILE_REQUESTS_KEY, JSON.stringify(data));
}

// --- The walkthrough: one matter from file request to finalisation ----------------------
//
// One made-up client, Zara Walkthrough, taken through a matter a step at a time. Each step
// sets her file request, her Aid Planner matter and her diary row to what they would be at
// that point, and says what changed and where to look.

export const WALKTHROUGH_KEY = "cls_lacw_local_test_walkthrough_v1";
const WALK_MATTER_ID = "walk-zara";

// Zara as she appears everywhere else in test mode: the sample letters in
// public/demo-documents/ carry this grant number, and the Actionstep simulator has her
// matter under this number, so a letter or a note can be filed on her Actionstep matter.
export const WALKTHROUGH_CLIENT = {
  name: "Zara Walkthrough",
  aidNumber: "26A900777",
  fileNumber: "DEMO-1004",
  actionstepMatterId: "10455",
};

export const WALKTHROUGH_ACTIONSTEP_MATTER = {
  externalId: WALKTHROUGH_CLIENT.actionstepMatterId,
  client: { fullName: WALKTHROUGH_CLIENT.name, fileNumber: WALKTHROUGH_CLIENT.fileNumber },
  matter: {
    summary: "Summary hearing (contest)",
    court: "Melbourne Magistrates' Court",
    appearanceType: "Contested hearing",
    nextAppearanceDate: "2026-10-08",
    fundingType: "Legal Aid",
    grantOfAidNumber: WALKTHROUGH_CLIENT.aidNumber,
  },
};
const WALK_REQUEST_ID = "walk-req-zara";
const WALK_ROW_ID = "walk-row-zara";

function walkMatter(today, { listing, listingInDays, stage, claims = [], aid = {}, funding = {}, application = {}, finalisation = {} }) {
  const planner = matter(WALK_MATTER_ID, WALKTHROUGH_CLIENT.name, listingInDays, today, {
    listing,
    claims,
    grant: stage !== "applied",
    aid,
    funding,
    application,
    notes: "Walkthrough matter. Use the walkthrough bar at the top of the page to move it on.",
  });
  if (stage === "applied") {
    Object.assign(planner.aid, { applicationStatus: "Submitted", aidNumber: "" });
    Object.assign(planner.funding, { aidApplied: true, grantInPlace: false });
    Object.assign(planner.application, { formCompleted: true, lodgedInAtlas: true });
  }
  Object.assign(planner.finalisation, finalisation);
  planner.client.fileNumber = WALKTHROUGH_CLIENT.fileNumber;
  if (stage !== "applied") planner.aid.aidNumber = WALKTHROUGH_CLIENT.aidNumber;
  return planner;
}

const contestDays = (n) => Array(n).fill("DEFEND/CONTEST DAILY FEE");

export const WALKTHROUGH_STEPS = [
  {
    title: "A new file request arrives",
    happened: "LACW receives an email asking for a new file for Zara Walkthrough.",
    look: ["Aid Planner → Applications: Zara is a New file request, with Start matter (no matter yet)."],
    request: { status: "" },
    matter: null,
    diary: null,
  },
  {
    title: "The application is lodged in ATLAS",
    happened: "The application is lodged and the request moves to Aid submitted. Her first mention is in 14 days.",
    look: [
      "Applications: Zara is Aid submitted, with Open matter.",
      "Aid Planner: no reminder. The application is in and VLA hasn't decided.",
      "Aid Status Diary: her MEN row fills itself in as Aid pending.",
    ],
    request: { status: "aid_submitted" },
    matter: (today) => walkMatter(today, { stage: "applied", listing: "Mention", listingInDays: 14 }),
    diary: { code: "MEN", inDays: 14 },
  },
  {
    title: "VLA approves a summary grant (Table A)",
    happened: "The approval letter is uploaded: a Table A grant for a summary hearing. The matter is listed for a contest mention in 21 days.",
    look: [
      "Applications: choose the Aid approved filter; Zara is there. (Open shows only requests still waiting.)",
      "Aid Planner: nothing due. The grant has a contest mention fee.",
      "Aid Status Diary: her CMEN row fills in as Aid confirmed with the aid number. Nothing is due: every summary grant covers contest mentions.",
    ],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, { listing: "CONTEST MENTION", listingInDays: 21 }),
    diary: { code: "CMEN", inDays: 21 },
  },
  {
    title: "The contest runs: 4 of 5 days claimed",
    happened: "The contest has run for 4 days and each day is claimed. Day 5 is listed in 20 days.",
    look: [
      "Aid Planner: Check if extension needed, upcoming. Only 1 of 5 contest days is left, and this listing uses it.",
      "Open the matter: the Extension check says the same, with the flowchart filled in.",
      "Aid Status Diary: her CONT row is Aid confirmed, and the code raises Check if extension needed.",
    ],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, { listing: "DEFEND/CONTEST DAILY FEE", listingInDays: 20, claims: contestDays(4) }),
    diary: { code: "CONT", inDays: 20 },
  },
  {
    title: "All 5 days used, and a 6th is listed",
    happened: "Day 5 is claimed. The contest isn't finished, and another day is listed in 10 days.",
    look: [
      "Aid Planner: Extension required, apply now. All 5 contest days on the grant are claimed.",
      "Aid Status Diary: her CONT row fills in as Extension required, with the same reason.",
      "Try Add to Outlook: it saves a reminder for the apply-by date.",
    ],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, { listing: "DEFEND/CONTEST DAILY FEE", listingInDays: 10, claims: contestDays(5) }),
    diary: { code: "CONT", inDays: 10 },
  },
  {
    title: "The extension is requested",
    happened: "The extension is lodged in ATLAS, and Mark as applied is pressed on the Extension required reminder.",
    look: ["Aid Planner: the reminder changes to Chase VLA decision. There's nothing more to apply for; wait for VLA and follow up."],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, { listing: "DEFEND/CONTEST DAILY FEE", listingInDays: 10, claims: contestDays(5), funding: { extensionRequested: true } }),
    diary: { code: "CONT", inDays: 10 },
  },
  {
    title: "The extension is approved and the contest ends",
    happened: "VLA approves the extension (extension 1): uploading the extension letter records it. Day 6 runs and is claimed, and the matter is listed for sentence in 18 days.",
    look: [
      "Aid Planner: nothing due. The grant has a sentence fee.",
      "Aid Status Diary: her SENT row fills in as Aid confirmed.",
      "An approved extension covers the rest of its stage: had a 7th contest day been listed, nothing would be due. A listing in a new stage, like a committal or trial, gets its own check.",
    ],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, {
      listing: "Sentence",
      listingInDays: 18,
      claims: contestDays(6),
      aid: { extensionNumber: "1", extensions: [{ extensionNumber: "1", aidType: "Summary Single" }] },
      funding: { extensionRequested: true, extensionDecision: "Approved", extensionApprovedForStage: "Contested hearing" },
    }),
    diary: { code: "SENT", inDays: 18 },
  },
  {
    title: "Sentenced: the matter is finalised",
    happened: "Zara is sentenced, the sentence fee is claimed and the matter is marked finalised.",
    look: [
      "Aid Planner: no reminders for Zara. Finalised matters never raise one.",
      "That's the whole matter. Use Restart to go again, or Load reminders demo to see every kind of reminder at once.",
    ],
    request: { status: "aid_approved" },
    matter: (today) => walkMatter(today, {
      listing: "Sentence",
      listingInDays: -1,
      claims: [...contestDays(6), "APPEAR AT SENTENCE"],
      aid: { extensionNumber: "1", extensions: [{ extensionNumber: "1", aidType: "Summary Single" }] },
      funding: { extensionRequested: true, extensionDecision: "Approved", extensionApprovedForStage: "Contested hearing" },
      finalisation: { matterFinalised: true },
    }),
    diary: null,
  },
];

// Put Zara's request, matter and diary row into step `index`, leaving the rest of the demo.
export function applyWalkthroughStep({ client, device }, index, today = new Date()) {
  const step = WALKTHROUGH_STEPS[index];
  if (!step) throw new Error(`No walkthrough step ${index}`);

  const matters = JSON.parse(client.getItem(AID_PLANNER_KEY) || "{}");
  const planner = step.matter ? step.matter(today) : null;
  if (planner) {
    // Reminders marked done on an earlier step do not carry over; each step starts clean.
    matters[WALK_MATTER_ID] = planner;
  } else {
    delete matters[WALK_MATTER_ID];
  }
  client.setItem(AID_PLANNER_KEY, JSON.stringify(matters));

  const requests = readDemoFileRequests(device);
  const request = {
    message_id: WALK_REQUEST_ID,
    client_id: "lacw",
    subject: "New file: Zara Walkthrough vs Police (Melbourne)",
    snippet: "Walkthrough file request.",
    date: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30).toISOString(),
    notes: "",
    ...step.request,
  };
  requests.emails = [request, ...requests.emails.filter((email) => email.message_id !== WALK_REQUEST_ID)];
  writeDemoFileRequests(device, requests);

  let week;
  try {
    week = JSON.parse(device.getItem(LACW_DIARY_ACTIVE_KEY) || "null");
  } catch {
    week = null;
  }
  week = week && Array.isArray(week.entries) ? week : { version: 1, sourceName: "Walkthrough week (made-up data)", savedAt: today.toISOString(), entries: [] };
  const others = week.entries.filter((entry) => entry.id !== WALK_ROW_ID);
  const row = step.diary
    ? [{
      id: WALK_ROW_ID,
      date: auDate(addDays(today, step.diary.inDays)),
      clientName: "WALKTHROUGH, Zara",
      court: "Melbourne Magistrates' Court",
      appearanceType: step.diary.code,
      lawyer: "EM",
      grantType: "",
      aidStatus: "Not checked",
      aidNumber: "",
      confirmed: false,
      confirmedBy: "",
      confirmedAt: "",
      notes: "Walkthrough row: fills itself in from Zara's Aid Planner matter.",
    }]
    : [];
  device.setItem(LACW_DIARY_ACTIVE_KEY, JSON.stringify({ ...week, entries: [...row, ...others] }));

  device.setItem(WALKTHROUGH_KEY, JSON.stringify({ step: index }));
  return step;
}

export function readWalkthroughStep(device) {
  try {
    const saved = JSON.parse(device.getItem(WALKTHROUGH_KEY) || "null");
    return Number.isInteger(saved?.step) ? saved.step : null;
  } catch {
    return null;
  }
}

export function endWalkthrough(device) {
  device.removeItem(WALKTHROUGH_KEY);
}

// Start the walkthrough on a clean slate: only Zara in this browser's test data.
export function startWalkthrough({ client, device }, today = new Date()) {
  client.setItem(AID_PLANNER_KEY, "{}");
  client.removeItem(AID_PLANNER_LAST_OPENED_KEY);
  device.setItem(LACW_DIARY_ACTIVE_KEY, JSON.stringify({ version: 1, sourceName: "Walkthrough week (made-up data)", savedAt: today.toISOString(), entries: [] }));
  writeDemoFileRequests(device, { emails: [], drafts: [] });
  return applyWalkthroughStep({ client, device }, 0, today);
}
